import { useEffect, useRef, useState } from "react";
import styles from "./FollowToast.module.css";

export interface FollowNotice {
  id: number;
  tone: "success" | "error";
  message: string;
}

interface FollowToastProps {
  notice: FollowNotice | null;
  onDismiss: () => void;
}

const VISIBLE_MS: Record<FollowNotice["tone"], number> = { success: 5_000, error: 10_000 };

/**
 * Confirmation or failure for a follow write. Failures are assertive (role="alert") because
 * the toggle has already rolled back; confirmations are polite. Auto-dismisses; hover or focus
 * pauses the timer for that notice.
 */
export default function FollowToast({ notice, onDismiss }: FollowToastProps) {
  const [pausedId, setPausedId] = useState<number | null>(null);
  // Parents pass a new callback each render; read it through a ref so that doesn't restart the timer.
  const dismissRef = useRef(onDismiss);
  useEffect(() => {
    dismissRef.current = onDismiss;
  });

  const id = notice?.id;
  const tone = notice?.tone;
  const paused = id !== undefined && pausedId === id;

  useEffect(() => {
    if (id === undefined || tone === undefined || paused) return;
    const timer = setTimeout(() => dismissRef.current(), VISIBLE_MS[tone]);
    return () => clearTimeout(timer);
  }, [id, tone, paused]);

  if (!notice) return null;
  const isError = notice.tone === "error";
  return (
    <div
      key={notice.id}
      role={isError ? "alert" : "status"}
      aria-live={isError ? "assertive" : "polite"}
      className={isError ? styles.error : styles.success}
      onPointerEnter={() => setPausedId(notice.id)}
      onPointerLeave={() => setPausedId(null)}
      onFocus={() => setPausedId(notice.id)}
      onBlur={() => setPausedId(null)}
    >
      <span>{notice.message}</span>
      <button type="button" className={styles.dismiss} onClick={onDismiss}>
        Dismiss
      </button>
    </div>
  );
}
